import React from "react"
import BarChart from "shared/ui/Charts/BarChart.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { TransformedTableData } from "utils/convertDataToTable"
import { CHART_DISPLAY_TYPE } from "types/ReportingDashboard/elements.types"

/**
 * Props for ChartElementDisplayArea component
 */
export interface ChartElementDisplayAreaProps {
  /** Whether chart data exists */
  hasChartData: boolean
  /** Whether component is in selection mode */
  showSelects: boolean
  /** Whether component is loading */
  isLoading: boolean
  /** Whether component is queued for execution */
  isQueuedForExecution: boolean
  /** Error message if any */
  error: string | null
  /** Whether config is initializing */
  isInitializing: boolean
  /** Whether component has attempted to load data */
  hasAttemptedLoad: boolean
  /** Converted chart data */
  convertedChartData: TransformedTableData | null
  /** Selected primary variable name */
  selectedVariable: string | null
  /** Whether in dashboard mode */
  isDashboard: boolean
  /** Selected dashboard keys */
  selectedDashboardKeys: string[]
  /** Selected dashboard categories */
  selectedDashboardCategories: string[]
  /** Chart display type */
  chartType: CHART_DISPLAY_TYPE
  /** Whether to show totals */
  showTotals: boolean
  /** Element height for proper sizing */
  elementHeight?: number
  /** Refresh handler */
  onRefresh: () => void
  /** Set show totals handler */
  onSetShowTotals: (value: boolean) => void
}

/**
 * Chart Display Area Component
 *
 * Handles the chart rendering and display logic including:
 * - Chart container and sizing
 * - Loading states and overlays
 * - Error states
 * - No data states
 * - Chart component with proper data filtering
 */
const ChartElementDisplayArea: React.FC<ChartElementDisplayAreaProps> = ({
  hasChartData,
  showSelects,
  isLoading,
  isQueuedForExecution,
  error,
  isInitializing,
  hasAttemptedLoad,
  convertedChartData,
  selectedVariable,
  isDashboard,
  selectedDashboardKeys,
  selectedDashboardCategories,
  chartType,
  showTotals,
  elementHeight,
  onRefresh,
  onSetShowTotals,
}) => {
  if (showSelects) {
    return null // Don't render chart display when in selection mode
  }

  // Calculate height using TimeSeriesChart approach for consistent vertical resizing
  const topBottomPaddingRem = 0.5
  const renderHeight = elementHeight || 400
  const chartAreaHeight = renderHeight - topBottomPaddingRem * 2 * 16
  const calculatedChartHeight = Math.max(150, chartAreaHeight - 30)

  return (
    <div
      className="flex-grow flex flex-col relative"
      style={{ height: "100%" }}
    >
      {/* Error display */}
      {error && !isInitializing && (
        <div
          className="absolute top-2 left-2 right-2 z-10 bg-red-50 border border-red-200 text-red-600 px-2 py-1 rounded-sm text-xs shadow-sm"
          role="alert"
        >
          <div className="flex justify-between items-center">
            <p className="truncate mr-1">{error}</p>
            <DataQButton
              onClick={onRefresh}
              size="small"
              className="ml-1 flex-shrink-0"
              type="link"
            >
              Retry
            </DataQButton>
          </div>
        </div>
      )}

      {/* Chart display */}
      {hasChartData && (
        <div
          className="relative flex-grow w-full"
          style={{
            minHeight: "150px",
            backgroundColor: "#ffffff",
            overflow: "hidden",
            padding: "10px",
            boxSizing: "border-box",
          }}
        >
          {convertedChartData ? (
            <BarChart
              isChart={true}
              data={convertedChartData.barChartData.data}
              keys={convertedChartData.barChartData.keys}
              statisticTypes={convertedChartData.barChartData.statisticTypes}
              xAxisLabel={
                selectedVariable?.split("//").pop() || "Primary Variable"
              }
              isDashboard={isDashboard}
              dashboardSelectedKeys={selectedDashboardKeys}
              dashboardSelectedCategories={selectedDashboardCategories}
              layout={
                chartType === CHART_DISPLAY_TYPE.HORIZONTAL_BAR
                  ? "horizontal"
                  : "vertical"
              }
              showTotals={showTotals}
              setShowTotals={onSetShowTotals}
              height={calculatedChartHeight}
            />
          ) : (
            <div className="p-4 text-gray-500">No chart data content</div>
          )}
        </div>
      )}

      {/* Loading overlay */}
      {(isLoading || isQueuedForExecution) && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-md z-20">
          <div className="text-blue-500 text-sm bg-blue-50 px-3 py-1 rounded-md shadow-sm">
            {isQueuedForExecution ? "Queued for execution..." : "Loading..."}
          </div>
        </div>
      )}

      {/* No data state */}
      {!hasChartData &&
        !isLoading &&
        !isQueuedForExecution &&
        !error &&
        !isInitializing &&
        hasAttemptedLoad && (
          <div className="flex-grow flex items-center justify-center h-full bg-gray-50 bg-opacity-80 rounded-md shadow-sm">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Data needed</p>
              <DataQButton onClick={onRefresh} type="primary" size="small">
                Retry
              </DataQButton>
            </div>
          </div>
        )}
    </div>
  )
}

export default ChartElementDisplayArea
